import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef } from "react";
import type { ErrorType, Fridge } from "../types";
import { captureApolloError } from "../utils/captureApolloError";
import { INITIAL_STATE_QUERY } from "../utils/gqlOperations";
import { logToSentry } from "../utils/logger";

// Mirrors useReconnectAfterBrokenSubscription's inner retry: 3 attempts, 1s apart. There is no
// outer backoff loop in THIS hook — on final failure onRecoveryFailed re-raises the Redux error
// state, so the NEXT healthy hourly poll re-creates the recovery edge; the hourly cadence is the
// outer retry loop.
const RECOVERY_ATTEMPT_LIMIT = 3;
const RECOVERY_RETRY_DELAY_MS = 1000;

type RecoverArgs = {
	fridgeErrorType: ErrorType | null;
	/**
	 * The provider's shouldSkipSubscription. When false the live stream is (or will be) up and the
	 * broken-subscription reconnect path owns recovery — firing here too would double-pull the
	 * initial state, so this hook only acts while the subscription is startup-skipped.
	 */
	isSubscriptionSkipped: boolean;
	fridgeId: string;
	refetch: () => Promise<{ data?: { fridge?: Fridge | null } }>;
	onData: (fridge: Fridge) => void;
	/**
	 * Called when EVERY attempt of one recovery flight failed, with the RETAINED error type this
	 * flight was recovering from — the provider re-raises it in Redux, so the error screen returns
	 * and the next healthy hourly poll fires a fresh true→false edge. The type is retained inside
	 * this hook (last non-null prop value) because nothing else survives a failing flight: a
	 * { fridge: null } completion overwrites the initial query's errored snapshot
	 * (refetchWritePolicy "overwrite"), so reading the error back from Apollo data at failure time
	 * finds nothing. Without the revert, the single edge would be consumed for good and one
	 * transient refetch failure would strand the kiosk without its subscription (partially healed
	 * meal/QR UI, stale state/door/transaction, no live updates).
	 */
	onRecoveryFailed: (errorType: ErrorType) => void;
};

/**
 * Recovers a fridge that BOOTED with a fridge error — CONFLICT, UNAUTHORIZED, or any other type —
 * on the initial query (JID-2257).
 *
 * On such a boot the startup gate (resolveShouldSkipSubscription) skips the live subscription and
 * nothing ever re-runs INITIAL_STATE_QUERY — the kiosk is stranded on the error screen even after
 * the backend recovers. The hourly price refetch DOES clear fridgeErrorType on a healthy response,
 * so this hook watches for that error→cleared edge while the subscription is still skipped and
 * re-pulls the initial state. The fresh, error-free response flips hasValidFridge, the startup
 * latch opens, and the subscription connects through the normal boot flow — no special restart
 * machinery needed.
 *
 * Failure handling: a flight retries in-band (3 attempts, 1s apart — the reconnect hook's inner
 * retry shape) on BOTH rejections and empty responses; unlike that hook there is no outer backoff
 * loop re-firing this lane, so giving up hands the episode back via onRecoveryFailed (see above).
 * A pending retry aborts silently when the subscription opened meanwhile — that lane owns state.
 *
 * Same defensive idioms as useReconnectAfterBrokenSubscription:
 * - prevRef edge detection, so mounting with the flag in either position never fires on its own.
 * - onData/refetch/onRecoveryFailed are kept current via refs, so a provider re-render storm (fresh
 *   arrow identities each render) can neither re-trigger the effect nor fire a stale closure.
 * - Single-flight guard: a flag flap while a flight (including its retries) is still in the air
 *   piggybacks on that flight; the guard releases at every terminal (success, give-up, abort).
 * - The retry timeout lives in a ref and is cleared on unmount.
 */
export const useRecoverFromFridgeError = ({
	fridgeErrorType,
	isSubscriptionSkipped,
	fridgeId,
	refetch,
	onData,
	onRecoveryFailed,
}: RecoverArgs): void => {
	const hasFridgeError = fridgeErrorType !== null;
	const onDataRef = useRef(onData);
	const refetchRef = useRef(refetch);
	const onRecoveryFailedRef = useRef(onRecoveryFailed);
	// Read by the retry callback, which would otherwise close over a stale value.
	const isSubscriptionSkippedRef = useRef(isSubscriptionSkipped);
	// Tracks the previous flag so the effect below can detect the true→false clearing edge.
	const prevHasFridgeErrorRef = useRef(hasFridgeError);
	// The error type this hook is recovering FROM — survives the hourly clear (and any Apollo data
	// overwrite by a failing refetch) so the terminal revert knows what to re-raise.
	const lastFridgeErrorTypeRef = useRef<ErrorType | null>(fridgeErrorType);
	if (fridgeErrorType !== null) {
		lastFridgeErrorTypeRef.current = fridgeErrorType;
	}
	const refetchInFlightRef = useRef(false);
	const retryTimeoutRef = useRef<number | null>(null);

	useEffect(() => {
		onDataRef.current = onData;
	}, [onData]);

	useEffect(() => {
		refetchRef.current = refetch;
	}, [refetch]);

	useEffect(() => {
		onRecoveryFailedRef.current = onRecoveryFailed;
	}, [onRecoveryFailed]);

	useEffect(() => {
		isSubscriptionSkippedRef.current = isSubscriptionSkipped;
	}, [isSubscriptionSkipped]);

	const runRecoveryRefetch = useCallback(
		async (attempt: number): Promise<void> => {
			if (!isSubscriptionSkippedRef.current) {
				// Another lane (reconnect refetch, live frame) opened the subscription while this flight
				// was retrying — it owns state now; stop without reverting the flag.
				refetchInFlightRef.current = false;
				return;
			}
			try {
				logToSentry(`Sending initial query after fridge error cleared on fridge: ${fridgeId}`, "warning");
				const res = await refetchRef.current();
				// Deliberately NO skip re-check between the await and onData: a SUCCESSFUL response
				// itself flips shouldSkipSubscription false (fresh error-free data opens the startup
				// latch) before this continuation runs, so such a guard discards its own snapshot —
				// Redux never gets state/door/transaction and a Restricted fridge renders as a welcome
				// screen (caught by the RESTRICTED recovery e2e). Applying the full snapshot is
				// snapshot-authoritative and safe; the pre-attempt check above still stops retries once
				// another lane owns recovery.
				if (res.data?.fridge) {
					onDataRef.current(res.data.fridge);
					refetchInFlightRef.current = false;
					return;
				}
				captureApolloError(
					new Error("Initial query error"),
					{
						kind: "Initial_Query_Error",
						query: INITIAL_STATE_QUERY.kind,
						variables: { fridgeId: fridgeId },
					},
					{
						ResponseData: res,
					},
				);
			} catch (err) {
				// Unlike the reconnect hook (whose rejections are re-fired by its outer backoff loop),
				// a rejection here must retry in-band — this flight is the only recovery this hour.
				Sentry.captureException(err);
			}
			if (attempt + 1 >= RECOVERY_ATTEMPT_LIMIT) {
				// The last attempt failed — give up inline (no pointless extra timer). The revert below
				// re-raises the error state, re-arming the hourly outer loop.
				captureApolloError(new Error(`Failed to recover initial fridge state ${RECOVERY_ATTEMPT_LIMIT} times`), {
					kind: "Initial_Query_Error_Retries_Limit",
					query: INITIAL_STATE_QUERY.kind,
					variables: { fridgeId: fridgeId },
				});
				if (lastFridgeErrorTypeRef.current !== null) {
					onRecoveryFailedRef.current(lastFridgeErrorTypeRef.current);
				}
				refetchInFlightRef.current = false;
				return;
			}
			retryTimeoutRef.current = window.setTimeout(() => {
				runRecoveryRefetch(attempt + 1);
			}, RECOVERY_RETRY_DELAY_MS);
		},
		[fridgeId],
	);

	useEffect(() => {
		const fridgeErrorCleared = prevHasFridgeErrorRef.current && !hasFridgeError;
		prevHasFridgeErrorRef.current = hasFridgeError;
		if (!(fridgeErrorCleared && isSubscriptionSkipped && fridgeId)) {
			return;
		}
		if (refetchInFlightRef.current) {
			return;
		}
		refetchInFlightRef.current = true;
		runRecoveryRefetch(0);
	}, [hasFridgeError, isSubscriptionSkipped, fridgeId, runRecoveryRefetch]);

	useEffect(
		() => () => {
			if (retryTimeoutRef.current !== null) {
				window.clearTimeout(retryTimeoutRef.current);
				retryTimeoutRef.current = null;
			}
		},
		[],
	);
};
